import React, { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const contactEmail = 'orders@shopnfctags.com';
  const supportEmail = 'support@shopnfctags.com';

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="text-gray-400 hover:text-white"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
          <div className="text-blue-500 font-bold text-xl">ShopNFCTags</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Have questions about our NFC products? Need help with your order? We're here to help!
          </p>
        </div>

        {/* Main CTA Card */}
        <div className="mb-16">
          <Card className="bg-gradient-to-br from-blue-600 to-cyan-600 border-0 overflow-hidden">
            <CardContent className="p-12 text-center">
              <Mail className="w-16 h-16 mx-auto mb-6 text-white" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Send Us an Email
              </h2>
              <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
                Click the button below to open your default email application and send us a message. We typically respond within 24 hours.
              </p>
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
                onClick={() => window.location.href = `mailto:${contactEmail}`}
              >
                <Send className="w-5 h-5 mr-2" />
                Email Us at {contactEmail}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {/* General Inquiries */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-blue-500/50 transition-colors">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="bg-blue-500/10 p-3 rounded-lg">
                  <Mail className="w-6 h-6 text-blue-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">General Inquiries</h3>
                  <p className="text-gray-400 mb-4">
                    Questions about products, pricing, or bulk orders
                  </p>
                  <a
                    href={`mailto:${contactEmail}`}
                    className="text-blue-500 hover:text-blue-400 transition-colors font-medium"
                  >
                    {contactEmail}
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Support */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-blue-500/50 transition-colors">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="bg-cyan-500/10 p-3 rounded-lg">
                  <Phone className="w-6 h-6 text-cyan-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">Customer Support</h3>
                  <p className="text-gray-400 mb-4">
                    Help with existing orders, returns, or technical issues
                  </p>
                  <a
                    href={`mailto:${supportEmail}`}
                    className="text-blue-500 hover:text-blue-400 transition-colors font-medium"
                  >
                    {supportEmail}
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Info Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {/* Business Hours */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center">
              <Clock className="w-8 h-8 mx-auto mb-4 text-blue-500" />
              <h4 className="text-lg font-semibold text-white mb-2">Business Hours</h4>
              <p className="text-gray-400 text-sm">
                Monday - Friday<br />
                9:00 AM - 6:00 PM EST
              </p>
            </CardContent>
          </Card>

          {/* Response Time */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center">
              <Send className="w-8 h-8 mx-auto mb-4 text-cyan-500" />
              <h4 className="text-lg font-semibold text-white mb-2">Response Time</h4>
              <p className="text-gray-400 text-sm">
                We typically respond to all<br />
                inquiries within 24 hours
              </p>
            </CardContent>
          </Card>

          {/* Location */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 text-center">
              <MapPin className="w-8 h-8 mx-auto mb-4 text-orange-500" />
              <h4 className="text-lg font-semibold text-white mb-2">Location</h4>
              <p className="text-gray-400 text-sm">
                United States<br />
                Serving customers worldwide
              </p>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Callout */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Have a Quick Question?
          </h3>
          <p className="text-gray-300 mb-6">
            Check out our FAQ section for instant answers to common questions about NFC products, shipping, and more.
          </p>
          <Button
            variant="outline"
            className="border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white"
            onClick={() => navigate('/#faq')}
          >
            View FAQ
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © 2026 ShopNFCTags. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
